import React from 'react';
import CurrencyFormat from '../Helpers/CurrencyFormat';
import { Spinner } from '@fluentui/react-components';
import { IOrderDetails } from '../../Api/Models/Order';
import { useGetStorefrontQuery } from '../../Api/Api';
import { parseCheckoutPageConfig } from '../Helpers/checkoutPageConfig';
import { elementStyleFrom, importantStyleRef } from '../Helpers/searchPageConfig';
import { useLocalization } from '../Helpers/useLocalization';

interface IOrdertableProps {
    order: IOrderDetails;
    taxAmount?: number;
    calculatingTax?: boolean;
    calculatingShipping?: boolean;
    shippingAmount?: number;
}

const Ordertable: React.FC<IOrdertableProps> = (props) => {
    const subtotal = (props.order?.products.reduce((acc, product) => acc + (product.price || 0) * product.quantity, 0) ?? 0) +
        (props.order?.bundles.reduce((acc, bundle) => acc + (bundle.price || 0) * bundle.quantity, 0) ?? 0);
    const total = subtotal + (props.taxAmount ?? 0) + (props.shippingAmount ?? 0);
    const storefrontSettings = useGetStorefrontQuery();
    const L = useLocalization();
    // Merchant checkout-page styling for the totals table's final row. The dark/light themes
    // force table-row colors with !important, so apply the merchant's style through an !important ref.
    const checkoutConfig = React.useMemo(() => parseCheckoutPageConfig(storefrontSettings.data?.checkoutPageConfig), [storefrontSettings.data?.checkoutPageConfig]);
    const totalRowRef = React.useMemo(() => { const st = elementStyleFrom(checkoutConfig.totalRow); return Object.keys(st).length ? importantStyleRef(st) : undefined; }, [checkoutConfig.totalRow]);
    return (<div>
        <table className='order-table'>
            <thead>
                <tr>
                    <th>{L('localization_cart_col_product')}</th>
                    <th style={{width: '50px', textAlign: 'center'}}>{L('localization_cart_col_quantity')}</th>
                    <th style={{width: '100px'}}>{L('localization_order_col_total')}</th>
                </tr>
            </thead>
            <tbody className='order-table-body'>
                {props.order.products.map((product) => (
                    <tr key={product.id}>
                        <td>{product.name}</td>
                        <td style={{textAlign: 'center'}}>{product.quantity}</td>
                        <td style={{textAlign: 'right'}}>
                            {product.price && <CurrencyFormat value={product.price * product.quantity}/>}
                        </td>
                    </tr>
                ))}
                {props.order.bundles.map((bundle) => (
                    <tr key={bundle.id}>
                        <td>{bundle.name}</td>
                        <td style={{textAlign: 'center'}}>{bundle.quantity}</td>
                        <td style={{textAlign: 'right'}}>
                            {bundle.price && <CurrencyFormat value={bundle.price * bundle.quantity}/>}
                        </td>
                    </tr>
                ))}
            </tbody>
            <tbody className='order-table-footer'>
                <tr>
                    <td colSpan={2}>{L('localization_order_subtotal')}</td>
                    <td>
                        {subtotal && <CurrencyFormat value={subtotal}/>}
                    </td>
                </tr>
                <tr>
                    <td colSpan={2}>{L('localization_order_estimated_tax')}</td>
                    <td>
                        {!props.calculatingTax && props.taxAmount !== 0 && props.taxAmount != null && <CurrencyFormat value={props.taxAmount}/>}
                        {!props.calculatingTax && (props.taxAmount === 0 || props.taxAmount === null) && L('localization_order_tax_to_be_added')}

                        {props.calculatingTax && <Spinner size='extra-tiny' />}
                    </td>
                </tr>
                {props.shippingAmount !== null && props.shippingAmount !== 0 && !props.calculatingShipping && <tr>
                    <td colSpan={2}>{L('localization_order_estimated_shipping')}</td>
                    <td style={{textAlign: 'right'}}>
                        {props.shippingAmount && <CurrencyFormat value={props.shippingAmount}/>}
                    </td>
                </tr>}
                {
                    props.calculatingShipping && <tr>
                        <td colSpan={2}>{L('localization_order_calculating_shipping')}</td>
                        <td style={{textAlign: 'right'}}>
                            <Spinner size='extra-tiny' />
                        </td>
                    </tr>
                }
                <tr className='order-table-total-row' ref={totalRowRef}>
                    <td colSpan={2}>{L('localization_order_total')}</td>
                    <td >
                        {!props.calculatingTax && total && <CurrencyFormat value={total}/>}
                        {props.calculatingTax && <Spinner size='extra-tiny' />}
                    </td>
                </tr>
            </tbody>
        </table>
    </div>);
}

export default Ordertable;